/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	ActionIcon,
	Flex,
	Modal,
	rem,
	Stack,
} from '@mantine/core';
import { PropsWithChildren } from 'react';

import { Title } from '@components/v2/ui';
import { Text } from '@components/v2/ui/Text';
import { IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */
type TEmailChangeVerificationModalProps =
	PropsWithChildren<{
		opened: boolean;
		onClose: () => void;
		expiresInMinutes?: number;
	}>;

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const EmailChangeVerificationModal = ({
	opened,
	onClose,
	expiresInMinutes = 30,
}: TEmailChangeVerificationModalProps) => {
	const { t } = useTranslation();

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			centered
			withCloseButton={false}
			radius="lg"
			size="lg"
			padding="xl"
			overlayProps={{
				opacity: 0.55,
				blur: 2,
			}}
			styles={{
				body: {
					padding: rem(32),
				},
			}}
		>
			<Stack gap="md" mt={-rem(16)}>
				<Flex justify={'space-between'} align={'center'}>
					<Title order={6} c={'#1D3965'}>
						{t('emailChange.title')}
					</Title>

					<ActionIcon
						variant="outline"
						onClick={onClose}
						radius="xl"
						h={48}
						w={48}
						aria-label={t('global.close')}
					>
						<IconX color="#727272" size={20} />
					</ActionIcon>
				</Flex>

				<Text variant="body1" c={'#505058'}>
					{t('emailChange.verificationSent')}
				</Text>

				<Text variant="button" c={'#505058'}>
					{t('emailChange.clickToConfirm')}
				</Text>

				<Text variant="body2" c={'#FF381C'}>
					{t('emailChange.expiresIn', {
						minutes: expiresInMinutes,
					})}
				</Text>
			</Stack>
		</Modal>
	);
};

export default EmailChangeVerificationModal;
